import {createAsyncThunk} from '@reduxjs/toolkit';
import getMarketSnapshot from 'api/facilities/get-market-snapshot';
import getMicroclimateMetadata from 'api/facilities/get-microclimate';

/**
 * Thunk action to fetch and store microclimate metadata for a specific facility.
 *
 * This function dispatches an asynchronous request to retrieve microclimate metadata
 * associated with a given facility ID. If successful, the received data and facility ID
 * are returned as the payload. In case of an error, the error is handled and
 * appropriately rejected.
 *
 * Action Type: 'facilities/storeFacilityMicroclimate'
 *
 * API Service: {@link getMicroclimateMetadata}
 */
export const getFacilityMicroclimateMetaData = createAsyncThunk(
    'facilities/storeFacilityMicroclimate',
    /**
     * @param {string} facilityId - The ID of the facility for which to fetch microclimate metadata.
     * @param {Object} param1 - An object containing utilities for thunk operations, including rejectWithValue.
     * @param {Function} param1.rejectWithValue - A utility function to handle rejections and return the rejected value.
     * @returns {Object} - A payload containing the facilityId and the response data on success, or an error on failure.
     */
    async (facilityId, {rejectWithValue}) => {
        try {
            const response = await getMicroclimateMetadata(facilityId);
            const payload = {facilityId, response};

            return payload;
        } catch (err) {
            console.error('storeFacilityMicroclimate error:', err);

            return rejectWithValue(err);
        }
    }
);

/**
 * Async thunk action to retrieve microclimate snapshot data for a specific facility.
 *
 * This action makes an asynchronous request to fetch the microclimate snapshot data
 * for the given facility ID. If the request is successful, it returns an object containing
 * the facility ID and the response data. If the request fails, it logs the error and
 * returns the rejection with the error message.
 *
 * Action Type: 'facilities/getFacilityMicroclimateSnapshotData'
 *
 * API Service: {@link getMarketSnapshot}
 *
 */
export const getFacilityMicroclimateSnapshotData = createAsyncThunk(
    'facilities/getFacilityMicroclimateSnapshotData',
    /**
     * @param {string} facilityId - The ID of the facility for which to retrieve microclimate snapshot data.
     * @param {object} thunkAPI - The thunk API object including dispatch and getState methods, along with rejectWithValue.
     * @returns {Promise<object>} The payload containing facilityId and response data on success, or an error on failure.
     */
    async (facilityId, {rejectWithValue}) => {
        try {
            const response = await getMarketSnapshot(facilityId);
            const payload = {facilityId, response};

            return payload;
        } catch (err) {
            console.error('getFacilityMicroclimateSnapshotData error:', err);

            return rejectWithValue(err);
        }
    }
);
